import { useCallback, useEffect, useState } from 'react'
import { Badge, BadgeProps, Button, Chip, Stack, styled, Tab } from '@mui/material'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import PageWrapper from 'hoc/PageWrapper'
import { useParams } from 'react-router-dom'
import {
  tenderApi,
  useNotificationsPollQuery,
  useTenderQuery,
} from 'services/tender'
import {
  useAcceptTermsConditionsMutation,
  useTermsConditionsStatusQuery,
} from 'services/terms-conditions'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import { TenderParticipationGeneralInfo } from './tender-participation-general-info'
import { StickyHeader } from 'components/StickyHeader'
import { ETenderStatus, ITender } from 'types/tender'
import {
  useConfirmTenderEvaluationMutation,
  useCreateTenderEvaluationMutation,
  useRestartTenderEvaluationMutation,
  useSubmitTenderEvaluationMutation,
} from 'services/tender/tenderEvaluation'
import TenderEvaluation from './tender-evaluation'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { TenderContestResult } from './tender-contest-result'
import { submissionEvaluationApi } from 'services/submissions/submission-evaluation'
import { QuestionAndAnswers } from 'pages/tender/question-answers/question-answers'
import { RequiredDocsSubmissionTab } from './required-docs-submission'
import { useUserCurrentQuery } from 'services/users'
import { submissionApi } from 'services/submissions'
import { lotsApi } from 'services/lots'
import { EOrganizationType, EUserRoles } from 'types/users'
import { formatTimeStampToDate, isBeforeGivenDate } from 'hooks/date/formattedTime'
import { ControlPanel } from './control-panel'
import { useTenderPassedStartTime } from 'hooks/date/useTenderPassedStartTime'
import { hasTenderPassedDocDeadline } from 'utils/tender'
import { FinalProtocol } from './final-protocol'
import { useGetTenderTabs } from 'hooks/useTenderTabs'

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -6,
    top: -5,
  },
}))

export enum TenderTabValues {
  GENERAL = 'GENERAL',
  QUESTION_ANSWERS = 'QUESTION_ANSWERS',
  CONTROL_PANEL = 'CONTROL_PANEL',
  REQUIRED_DOCS = 'REQUIRED_DOCS',
  EVALUATION = 'EVALUATION',
  CONTEST_RESULT = 'CONTEST_RESULT',
  FINAL_PROTOCOL = 'FINAL_PROTOCOL',
}

type Props = {
  tender: ITender
}

const TenderParticipation = ({ tender }: Props) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<TenderTabValues>(TenderTabValues.GENERAL)
  const passedStartDate = useTenderPassedStartTime(tender)
  const [isSubmissionTimePassed, setIsSubmissionTimePassed] = useState(true)

  const dispatch = useDispatch()

  const tenderId = tender.id
  const { data: termsStatus, isLoading } = useTermsConditionsStatusQuery(
    {
      tenderId,
    },
    { skip: !tenderId }
  )

  const { data: pollData } = useNotificationsPollQuery(
    {
      tenderId: tender.id,
    },
    {
      skip: !tender.id,
      pollingInterval: 60000,
    }
  )

  const { data: user, isLoading: isUserLoading } = useUserCurrentQuery()

  const [acceptTermsConditions] = useAcceptTermsConditionsMutation()
  const [
    createTenderEvaluation,
    { isLoading: isTenderEvaluationLoading, isError, isSuccess },
  ] = useCreateTenderEvaluationMutation()

  const [
    createTenderWinnerSubmit,
    {
      isLoading: isTenderWinnerSubmitLoading,
      isError: isTenderWinnerSubmitError,
      isSuccess: isTenderWinnerSubmitSuccess,
    },
  ] = useSubmitTenderEvaluationMutation()

  const [
    restartEvaluation,
    {
      isLoading: isRestartEvaluationLoading,
      isError: isRestartEvaluationError,
      isSuccess: isRestartEvaluationSuccess,
    },
  ] = useRestartTenderEvaluationMutation()

  const [
    sendToCommission,
    {
      isLoading: isSendToCommisionLoading,
      isError: isSendToCommisionError,
      isSuccess: isSendToCommisionSuccess,
    },
  ] = useConfirmTenderEvaluationMutation()

  const isTenderAuthority =
    user?.organizationType &&
    user.organizationType === EOrganizationType.TENDER_AUTHORITY
  const tenderTabs = useGetTenderTabs(tender, t, isTenderAuthority)

  useEffect(() => {
    if (
      isSuccess ||
      isTenderWinnerSubmitSuccess ||
      isRestartEvaluationSuccess ||
      isSendToCommisionSuccess
    ) {
      toast.success(t('operation-success'))
    }

    if (
      isError ||
      isTenderWinnerSubmitError ||
      isRestartEvaluationError ||
      isSendToCommisionError
    ) {
      toast.error(t('operation-failure'))
    }
  }, [
    isTenderWinnerSubmitError,
    isError,
    isSuccess,
    isTenderWinnerSubmitSuccess,
    isRestartEvaluationError,
    isSendToCommisionError,
    isRestartEvaluationSuccess,
    isSendToCommisionSuccess,
    t,
  ])

  const checkSubmissionDate = useCallback(() => {
    if (tender?.docSubmissionDeadline) {
      setIsSubmissionTimePassed(hasTenderPassedDocDeadline(tender))
    }
  }, [tender?.docSubmissionDeadline])

  useEffect(() => {
    const intervalId = setInterval(checkSubmissionDate, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [checkSubmissionDate])

  if (
    isLoading ||
    isUserLoading ||
    isTenderEvaluationLoading ||
    isTenderWinnerSubmitLoading ||
    isRestartEvaluationLoading ||
    isSendToCommisionLoading
  )
    return <Loading fullPage={false} />
  if (termsStatus === undefined) {
    return <></>
  }
  const isTermsAccepted = termsStatus.accepted

  const startEvaluation = async () => {
    await createTenderEvaluation({ tenderId: tender.id })
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
  }
  const tenderWinner = async () => {
    await createTenderWinnerSubmit({
      tenderId,
    })
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
    dispatch(submissionEvaluationApi.util.invalidateTags(['SubmissionEvaluations']))
  }
  const handleRestartEvaluation = async () => {
    await restartEvaluation({
      tenderId,
    })
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
    setTab(TenderTabValues.EVALUATION)
  }
  const handleSendToCommission = async () => {
    await sendToCommission({
      tenderId,
    })
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
  }

  const canStartEvaluation =
    isTenderAuthority &&
    user?.userRoles?.some((role) =>
      [EUserRoles.USER, EUserRoles.PROCUREMENT_SPECIALIST].includes(role)
    ) &&
    isBeforeGivenDate(tender.envelopeOpeningTime) &&
    (tender.status === ETenderStatus.PUBLISHED ||
      tender.status === ETenderStatus.MODIFIED)

  const canSeeControlPanel = isTenderAuthority

  const canSeeSendToComissionBtn =
    isTenderAuthority && tender.status === ETenderStatus.WINNER_SELECTED

  const canSeeRestartEvaluationBtn =
    isTenderAuthority &&
    (tender.status === ETenderStatus.EVALUATION_SUBMITTED ||
      tender.status === ETenderStatus.WINNER_SELECTED)

  const canSeeFinalProtocol = isTenderAuthority

  const canStartWinnerSelection =
    isTenderAuthority && tender.status === ETenderStatus.UNDER_EVALUATION

  return (
    <PageWrapper>
      <Stack spacing={2}>
        <StickyHeader
          title={`${t(`${tender.method}_title`)} - ${tender.name}, Başlama tarixi: ${formatTimeStampToDate(tender.startTime)}`}
          headerActions={
            <Stack direction="row" spacing={1}>
              {canSeeRestartEvaluationBtn && (
                <>
                  <Button
                    onClick={handleRestartEvaluation}
                    size="small"
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    sx={{
                      backgroundColor: '#f14433',
                      '&:hover': { background: '#f14433' },
                    }}
                  >
                    {t('restart-evaluation')}
                  </Button>
                </>
              )}
              {canSeeSendToComissionBtn && (
                <Button
                  size="small"
                  variant="contained"
                  style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                  onClick={handleSendToCommission}
                >
                  {t('send-to-commission')}
                </Button>
              )}
              {canStartWinnerSelection && (
                <Button
                  onClick={tenderWinner}
                  size="small"
                  variant="contained"
                  style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                >
                  {t('start-winner')}
                </Button>
              )}
              {canStartEvaluation && (
                <Button
                  onClick={startEvaluation}
                  size="small"
                  variant="contained"
                  style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                >
                  {t('start-evaluation')}
                </Button>
              )}
              <Chip
                label={t(`tender-status-${tender.status}`)}
                color={
                  tender.status === ETenderStatus.COMPLETED ? 'success' : 'primary'
                }
                sx={{ color: '#fff', textTransform: 'uppercase' }}
              />
              {passedStartDate &&
                !isTermsAccepted &&
                tender.status !== ETenderStatus.EVALUATION_SUBMITTED &&
                user?.organizationType === EOrganizationType.CLIENT &&
                !isSubmissionTimePassed && (
                  <Button
                    onClick={() =>
                      acceptTermsConditions({
                        tenderId,
                      })
                    }
                    size="small"
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                  >
                    {t('confirmConditions')}
                  </Button>
                )}
            </Stack>
          }
        />
        <TabContext value={tab}>
          <TabList
            onChange={(_event, newValue) => {
              setTab(newValue)
              if (newValue === TenderTabValues.EVALUATION) {
                dispatch(
                  submissionEvaluationApi.util.invalidateTags([
                    'SubmissionEvaluation',
                  ])
                )
              }
              if (newValue === TenderTabValues.REQUIRED_DOCS) {
                dispatch(lotsApi.util.invalidateTags(['SingleLot']))
                dispatch(submissionApi.util.invalidateTags(['MySubmissions']))
              }
            }}
            sx={{
              '&& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
          >
            {tenderTabs
              .filter((item) => !item.isDisabled)
              .map(({ value, label }) => {
                if (value === TenderTabValues.QUESTION_ANSWERS) {
                  return (
                    <Tab
                      sx={{ background: '#FFF' }}
                      key={value}
                      label={
                        <StyledBadge
                          color="error"
                          variant={
                            pollData?.hasAnyUnreadMessage ? 'dot' : 'standard'
                          }
                        >
                          {label}
                        </StyledBadge>
                      }
                      value={value}
                    />
                  )
                }
                return (
                  <Tab
                    sx={{ background: '#FFF' }}
                    key={value}
                    label={label}
                    value={value}
                  />
                )
              })}
          </TabList>
          <TabPanel value={TenderTabValues.GENERAL}>
            <TenderParticipationGeneralInfo
              passedStartDate={passedStartDate}
              tender={tender}
              isTermsAccepted={isTermsAccepted}
              user={user}
              isSubmissionTimePassed={isSubmissionTimePassed}
            />
          </TabPanel>
          <TabPanel value={TenderTabValues.QUESTION_ANSWERS}>
            <QuestionAndAnswers tender={tender} pollData={pollData} />
          </TabPanel>
          {canSeeControlPanel && (
            <TabPanel value={TenderTabValues.CONTROL_PANEL}>
              <ControlPanel tender={tender} />
            </TabPanel>
          )}
          <TabPanel value={TenderTabValues.REQUIRED_DOCS}>
            <RequiredDocsSubmissionTab tender={tender} />
          </TabPanel>
          <TabPanel value={TenderTabValues.EVALUATION}>
            <TenderEvaluation tender={tender} />
          </TabPanel>
          <TabPanel value={TenderTabValues.CONTEST_RESULT}>
            <TenderContestResult tender={tender} />
          </TabPanel>
          {canSeeFinalProtocol && (
            <TabPanel value={TenderTabValues.FINAL_PROTOCOL}>
              <FinalProtocol tender={tender} />
            </TabPanel>
          )}
        </TabContext>
      </Stack>
    </PageWrapper>
  )
}

export const TenderParticipationPage = () => {
  const { tenderId } = useParams()

  const { data: tender, isLoading, isError } = useTenderQuery({ id: tenderId ?? '' })

  if (isLoading) return <Loading />
  if (isError) return <Error />

  if (tender === undefined) {
    return <></>
  }

  return <TenderParticipation tender={tender} />
}
